import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

const resources = {
  en: {
    translation: {
      // Navigation & Common
      solutions: 'Solutions',
      features: 'Features',
      howItWorks: 'How It Works',
      forBusiness: 'For Business',
      getStarted: 'Get Started',
      viewAll: 'View All',
      continue: 'Continue',
      back: 'Back',
      // Dashboard & Analytics
      dashboard: 'Dashboard',
      analytics: 'Analytics',
      inventory: 'Inventory',
      customers: 'Customers',
      risk: 'Risk',
      marketValue: 'Market Value',
      totalInventory: 'Total Inventory',
      revenue: 'Revenue',
      activeTraders: 'Active Traders',
      liveMarketTicker: 'Live Market Ticker',
      dailyMarketInsight: 'Daily Market Insight',
      recentTradeIn: 'Recent Trade-In Requests',
      deviceAssessments: 'Latest device trade-in submissions',
      // Market Stats
      marketStats: {
        verificationIssues: 'of used smartphone transactions are hindered by verification issues',
        manualGrading: 'of resellers waste time with manual grading processes',
        lostRevenue: 'in lost revenue due to inefficient resale market'
      },
      // Trade-in Process
      tradeIn: {
        deviceType: 'Device Type',
        brand: 'Brand',
        model: 'Model',
        condition: 'Condition',
        storage: 'Storage',
        color: 'Color',
        excellent: 'Excellent',
        good: 'Good',
        fair: 'Fair',
        selectDevice: 'Select Device Type',
        selectBrand: 'Select Brand',
        selectModel: 'Select Model',
        deviceCondition: 'Device Condition',
        estimatedQuote: 'Your Estimated Quote',
        acceptOffer: 'Accept Offer',
        tryAgain: 'Try Another Device'
      },
      // AI Grading
      aiGrading: {
        title: 'How Our AI Grading Works',
        subtitle: 'Our proprietary AI technology evaluates devices with precision and consistency, ensuring fair market value for every trade-in.',
        steps: {
          scan: {
            title: 'Device Scan',
            description: 'Our system captures high-resolution images of the device from multiple angles to detect physical condition.',
            features: [
              'Screen damage detection',
              'Body condition assessment',
              'Cosmetic grading',
              'Hardware verification'
            ]
          },
          diagnostic: {
            title: 'Diagnostic Analysis',
            description: 'Our AI runs comprehensive tests to evaluate all functional aspects of the device.',
            features: [
              'Battery health check',
              'Component functionality tests',
              'IMEI verification',
              'Performance analysis'
            ]
          },
          valuation: {
            title: 'Market Valuation',
            description: 'Our algorithm compares the device against current market data to determine a fair value.',
            features: [
              'Real-time market analysis',
              'Competitive pricing',
              'Instant quote generation',
              'Value optimization'
            ]
          }
        }
      },
      // Status & Conditions
      status: {
        pending: 'Pending',
        completed: 'Completed',
        processing: 'Processing',
        rejected: 'Rejected'
      },
      // Currency
      currencySymbol: '$',
      currencyCode: 'USD',
      marketChallenges: {
        title: {
          prefix: 'The Mobile Resale Market Faces',
          highlight: 'Unique Challenges'
        },
        subtitle: 'Current trade-in processes are manual, time-consuming, and prone to errors, leading to significant market inefficiencies.',
        stats: {
          verification: 'of used smartphone transactions are hindered by verification issues',
          manual: 'of resellers waste time with manual grading processes',
          revenue: 'in lost revenue due to inefficient resale market'
        },
        manual: {
          title: 'Manual Assessment',
          subtitle: 'Time-consuming, error-prone processes',
          description: 'Time-consuming, error-prone processes that result in inconsistent grading and lost value',
          issues: [
            'Inconsistent evaluations',
            'Time-intensive process',
            'Limited market visibility',
            'High risk of human error'
          ]
        },
        solution: {
          title: 'R3UP Automated Solution',
          subtitle: 'AI-powered precision',
          description: 'Fast, accurate, and consistent assessment that maximizes device value and customer trust',
          features: [
            {
              title: 'AI powered accuracy',
              description: 'Consistent and precise device evaluation'
            },
            {
              title: '90% faster assessment',
              description: 'Rapid processing of trade-in requests'
            },
            {
              title: 'Integrated wholesale access',
              description: 'Direct connection to verified buyers'
            }
          ]
        }
      },
      // Landing Hero Section
      hero: {
        badge: 'New All in One Solution 2025',
        title: {
          line1: 'The Future of',
          line2: 'Trade in Wholesale'
        },
        subtitle: {
          heading: 'A Complete Trading Ecosystem',
          description: 'Powerful analytics, risk assessment, and group buying tools for phone traders. Make smarter decisions with real-time market data.'
        },
        buttons: {
          demo: 'Schedule a Demo',
          learn: 'Learn More'
        }
      },
      // Trade-in Solutions
      tradeSolutions: {
        badge: 'Complete Solutions',
        title: {
          prefix: 'Comprehensive Trade-In',
          highlight: 'Solutions'
        },
        subtitle: 'End-to-end tools and services for every stage of the device lifecycle, from assessment to resale.',
        solutions: [
          {
            title: 'Consumer Trade-In',
            description: 'Get instant quotes for your used devices with our AI-powered assessment technology',
            features: ['Instant quotes', 'AI assessment', 'Secure payment']
          },
          {
            title: 'Self-Service Kiosks',
            description: 'In-store kiosk systems for automated device assessment and instant trade-in credit',
            features: ['24/7 availability', 'Instant credit', 'Easy setup']
          },
          {
            title: 'AI Diagnostics',
            description: 'Advanced visual and functional diagnostics to accurately assess device condition',
            features: ['Visual inspection', 'Component testing', 'IMEI check']
          },
          {
            title: 'Business Platform',
            description: 'Comprehensive tools for retailers and carriers to manage trade-in programs',
            features: ['Analytics', 'Inventory management', 'Reporting']
          },
          {
            title: 'Wholesale Market',
            description: 'Connect with verified buyers and sellers in our global device marketplace',
            features: ['Verified buyers', 'Secure trades', 'Global reach']
          },
          {
            title: 'Auction Platform',
            description: 'Maximize device value through competitive bidding in our auction environment',
            features: ['Live bidding', 'Bulk lots', 'Market insights']
          }
        ]
      },
      // Advanced Features
      advancedFeatures: {
        badge: 'ADVANCED FEATURES',
        title: {
          prefix: 'Powerful Technology Meets',
          highlight: 'Intuitive Design'
        },
        subtitle: 'Our platform combines cutting-edge AI technology with user-friendly interfaces to deliver a seamless trade-in experience.',
        features: [
          {
            title: 'AI-Powered Assessment',
            description: 'Get top value for your used electronics with our advanced visual diagnostic technology',
            features: ['Visual damage detection', 'Component testing', 'Market value analysis']
          },
          {
            title: 'Trusted Verification',
            description: 'Comprehensive IMEI checks and lock detection ensure secure, reliable transactions',
            features: ['IMEI verification', 'Blacklist checking', 'Ownership validation']
          },
          {
            title: 'Wholesale Marketplace',
            description: 'Connect with premium buyers and sellers in our global device marketplace',
            features: ['Global network access', 'Secure transactions', 'Competitive pricing']
          }
        ],
        stats: [
          {
            value: '99.9%',
            label: 'Uptime'
          },
          {
            value: '50M+',
            label: 'Devices Processed'
          },
          {
            value: '95%',
            label: 'Accuracy Rate'
          },
          {
            value: '2M+',
            label: 'Monthly Trades'
          }
        ]
      },
      // Trade-in Widget
      tradeInWidget: {
        title: 'Quick & Easy',
        heading: 'Seamless Trade-In Experience',
        subtitle: 'Get an instant quote for your device in just a few steps. Our process is designed to be fast, transparent, and convenient.',
        steps: {
          deviceType: {
            title: 'Select Device Type',
            smartphone: 'Smartphone',
            tablet: 'Tablet'
          },
          brand: {
            title: 'Select Brand',
            options: {
              apple: 'Apple',
              samsung: 'Samsung',
              google: 'Google',
              other: 'Other'
            }
          },
          model: {
            title: 'Select Model',
            options: {
              iphone13: 'iPhone 13 Pro',
              iphone12: 'iPhone 12',
              iphone11: 'iPhone 11',
              other: 'Other'
            }
          },
          condition: {
            title: 'Device Condition',
            options: {
              excellent: {
                title: 'Excellent',
                desc: 'Like new, no scratches or damage'
              },
              good: {
                title: 'Good',
                desc: 'Minor scratches, fully functional'
              },
              fair: {
                title: 'Fair',
                desc: 'Visible wear, works properly'
              }
            }
          },
          quote: {
            title: 'Your Estimated Quote',
            deviceInfo: 'This is an estimated trade-in value for your',
            condition: 'in',
            condition_suffix: 'condition',
            buttons: {
              accept: 'Accept Offer',
              tryAnother: 'Try Another Device'
            }
          }
        },
        whyUs: {
          title: 'Why Trade In With Us?',
          features: [
            {
              title: 'Fast & Convenient',
              description: 'Get an instant quote in minutes. No waiting, no hassle.'
            },
            {
              title: 'Best Value Guaranteed',
              description: 'Our AI-powered pricing ensures you get the highest market value for your device.'
            },
            {
              title: 'Secure & Trustworthy',
              description: 'Your data is wiped securely, and all transactions are protected.'
            }
          ]
        }
      },
      // AI Assessment
      aiAssessment: {
        title: 'AI-Powered Device Assessment',
        subtitle: 'Our advanced AI technology accurately evaluates device condition, ensuring fair and competitive trade-in values.',
        features: [
          'Visual inspection detects scratches, dents, and screen damage',
          'Functional testing for all device components',
          'Market-based pricing algorithms for maximum value',
          'Instant quotes in under 2 minutes'
        ]
      },
      // Diagnostic Platform
      diagnosticPlatform: {
        badge: 'See CH3CKTECH in action',
        subtitle: 'Our comprehensive diagnostic platform provides detailed device information, IMEI verification, and component testing to ensure every device is properly evaluated.',
        device: {
          info: {
            title: 'Device Information',
            model: 'Model',
            storage: 'Storage',
            color: 'Color',
            imei: 'IMEI'
          },
          verification: {
            title: 'IMEI Verification',
            status: {
              clean: 'Clean Status',
              notBlacklisted: 'Not Blacklisted',
              notLost: 'Not Reported Lost',
              complete: 'Verification Complete'
            }
          },
          testing: {
            title: 'Component Testing',
            components: {
              battery: 'Battery',
              camera: 'Camera',
              wifi: 'WiFi',
              bluetooth: 'Bluetooth',
              speakers: 'Speakers',
              charging: 'Charging',
              screen: 'Screen',
              vibration: 'Vibration'
            },
            status: {
              passed: 'Passed',
              warning: 'Warning',
              failed: 'Failed'
            }
          },
          condition: {
            title: 'Physical Condition',
            grade: 'Overall Grade',
            details: [
              'Minor scratches on back panel',
              'Small dent on bottom edge',
              'Screen in excellent condition'
            ]
          }
        }
      },
      // Management Dashboard
      managementDashboard: {
        badge: 'SAAS PLATFORM',
        title: 'Complete Trade-In Management',
        subtitle: ' Unified dashboard with diagnostics and reporting',
        description: 'Our comprehensive SaaS platform delivers easy business oversight over their trade-in programs with powerful analytics and management tools.',
        features: [
          'Customer management',
          'Device assessment tools',
          'Inventory management',
          'Inventory tracking',
          'Transaction processing',
          'Reporting & analytics',
          'Self-serve customer module'
        ],
        cta: 'Request Demo'
      },
      // Syndicate Auctions
      syndicateAuctions: {
        badge: 'Collective Buying Power',
        title: {
          prefix: 'Syndicate',
          highlight: 'Auctions'
        },
        subtitle: 'Join forces with other buyers to unlock premium inventory and higher profits',
        whatIs: {
          title: 'What is a',
          highlight: 'Syndicate',
          description: 'A syndicate in the world of mobile device auctions refers to multiple buyers pooling funds to bid collaboratively, allowing them to purchase larger lots of devices, access better prices, and share inventory based on pre-agreed terms.'
        },
        benefits: [
          {
            title: 'Lower Capital Requirements',
            description: 'Pool resources with other buyers to access larger inventory lots beyond individual purchasing power.'
          },
          {
            title: 'Better Economics of Scale',
            description: 'Gain leverage to negotiate lower per-unit prices and improved shipping rates.'
          },
          {
            title: 'Reduced Risk Exposure',
            description: 'Share the risk of defective or low-demand inventory across multiple syndicate members.'
          },
          {
            title: 'Access Premium Inventory',
            description: 'Target higher-quality device lots that would be financially out of reach individually.'
          }
        ],
        structure: {
          title: 'Syndicate Structure',
          roles: [
            {
              title: 'Lead/Organizer',
              description: 'Initiates the syndicate, manages bidding strategy and handles logistics'
            },
            {
              title: 'Financial Contributors',
              description: 'Pool capital, define budgets and determine risk tolerance levels'
            },
            {
              title: 'Distribution Partners',
              description: 'Handle local market resale, online channels, or specialized repairs'
            }
          ]
        }
      },
      // Footer
      footer: {
        description: 'The future of trade-in wholesale technology. Empowering businesses with AI-driven market insights.',
        sections: {
          solutions: {
            title: 'Solutions',
            links: [
              { label: 'Consumer Trade-In', href: '#consumer-trade-in' },
              { label: 'Business Platform', href: '#business-platform' },
              { label: 'Wholesale Marketplace', href: '#wholesale-marketplace' },
              { label: 'Auction Platform', href: '#auctions' }
            ]
          },
          company: {
            title: 'Company',
            links: [
              { label: 'About Us', href: '#about' },
              { label: 'Careers', href: '#careers' },
              { label: 'Press', href: '#press' },
              { label: 'Contact', href: '#contact' }
            ]
          },
          legal: {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '#privacy' },
              { label: 'Terms of Service', href: '#terms' },
              { label: 'Cookie Policy', href: '#cookies' }
            ]
          }
        },
        copyright: '© 2024 R3UP. All rights reserved.'
      }
    }
  },
  pt: {
    translation: {
      // Navigation & Common
      solutions: 'Soluções',
      features: 'Recursos',
      howItWorks: 'Como Funciona',
      forBusiness: 'Para Empresas',
      getStarted: 'Começar',
      viewAll: 'Ver Todos',
      continue: 'Continuar',
      back: 'Voltar',
      // Dashboard & Analytics
      dashboard: 'Painel',
      analytics: 'Análises',
      inventory: 'Inventário',
      customers: 'Clientes',
      risk: 'Risco',
      marketValue: 'Valor de Mercado',
      totalInventory: 'Inventário Total',
      revenue: 'Receita',
      activeTraders: 'Traders Ativos',
      liveMarketTicker: 'Cotações ao Vivo',
      dailyMarketInsight: 'Análise Diária do Mercado',
      recentTradeIn: 'Pedidos Recentes de Trade-In',
      deviceAssessments: 'Últimas avaliações de dispositivos',
      // Market Stats
      marketStats: {
        verificationIssues: 'das transações de smartphones usados são prejudicadas por problemas de verificação',
        manualGrading: 'dos revendedores perdem tempo com processos manuais de avaliação',
        lostRevenue: 'em receita perdida devido ao mercado de revenda ineficiente'
      },
      // Trade-in Process
      tradeIn: {
        deviceType: 'Tipo de Dispositivo',
        brand: 'Marca',
        model: 'Modelo',
        condition: 'Condição',
        storage: 'Armazenamento',
        color: 'Cor',
        excellent: 'Excelente',
        good: 'Bom',
        fair: 'Regular',
        selectDevice: 'Selecione o Tipo de Dispositivo',
        selectBrand: 'Selecione a Marca',
        selectModel: 'Selecione o Modelo',
        deviceCondition: 'Condição do Dispositivo',
        estimatedQuote: 'Sua Cotação Estimada',
        acceptOffer: 'Aceitar Oferta',
        tryAgain: 'Tentar Outro Dispositivo'
      },
      // AI Grading
      aiGrading: {
        title: 'Como Nossa Avaliação por IA Funciona',
        subtitle: 'Nossa tecnologia proprietária de IA avalia dispositivos com precisão e consistência, garantindo valor justo de mercado para cada trade-in.',
        steps: {
          scan: {
            title: 'Escaneamento do Dispositivo',
            description: 'Nosso sistema captura imagens em alta resolução do dispositivo de vários ângulos para detectar condições físicas.',
            features: [
              'Detecção de danos na tela',
              'Avaliação da condição física',
              'Classificação cosmética',
              'Verificação de hardware'
            ]
          },
          diagnostic: {
            title: 'Análise Diagnóstica',
            description: 'Nossa IA executa testes abrangentes para avaliar todos os aspectos funcionais do dispositivo.',
            features: [
              'Verificação da saúde da bateria',
              'Testes de funcionalidade dos componentes',
              'Verificação de IMEI',
              'Análise de desempenho'
            ]
          },
          valuation: {
            title: 'Avaliação de Mercado',
            description: 'Nosso algoritmo compara o dispositivo com dados atuais do mercado para determinar um valor justo.',
            features: [
              'Análise de mercado em tempo real',
              'Preços competitivos',
              'Geração instantânea de cotação',
              'Otimização de valor'
            ]
          }
        }
      },
      // Status & Conditions
      status: {
        pending: 'Pendente',
        completed: 'Concluído',
        processing: 'Processando',
        rejected: 'Rejeitado'
      },
      // Currency
      currencySymbol: 'R$',
      currencyCode: 'BRL',
      marketChallenges: {
        title: {
          prefix: 'O Mercado Brasileiro Enfrenta',
          highlight: 'Desafios Únicos'
        },
        subtitle: 'Os processos atuais de trade-in são manuais, demorados e sujeitos a erros, levando a ineficiências significativas no mercado.',
        stats: {
          verification: 'das transações de smartphones usados são prejudicadas por problemas de verificação',
          manual: 'dos revendedores perdem tempo com processos manuais de classificação',
          revenue: 'em receita perdida devido ao mercado de revenda ineficiente'
        },
        manual: {
          title: 'Avaliação Manual',
          subtitle: 'Processos demorados e sujeitos a erros',
          description: 'Processos demorados e sujeitos a erros que resultam em classificação inconsistente e perda de valor',
          issues: [
            'Avaliações inconsistentes',
            'Processo demorado',
            'Visibilidade limitada do mercado',
            'Alto risco de erro humano'
          ]
        },
        solution: {
          title: 'Solução Automatizada R3UP',
          subtitle: 'Precisão com IA',
          description: 'Avaliação rápida, precisa e consistente que maximiza o valor do dispositivo e a confiança do cliente',
          features: [
            {
              title: 'Precisão com IA',
              description: 'Avaliação consistente e precisa de dispositivos'
            },
            {
              title: '90% mais rápido',
              description: 'Processamento rápido de solicitações de trade-in'
            },
            {
              title: 'Acesso integrado ao atacado',
              description: 'Conexão direta com compradores verificados'
            }
          ]
        }
      },
      // Landing Hero Section
      hero: {
        badge: 'Nova Convenção de Trading 2024',
        title: {
          line1: 'O Futuro do',
          line2: 'Comércio Atacadista'
        },
        subtitle: {
          heading: 'Um Ecossistema Completo de Trading',
          description: 'Análises poderosas, avaliação de risco e ferramentas de compra em grupo para comerciantes de telefones. Tome decisões mais inteligentes com dados de mercado em tempo real.'
        },
        buttons: {
          demo: 'Agendar Demo',
          learn: 'Saiba Mais'
        }
      },
      // Trade-in Solutions
      tradeSolutions: {
        badge: 'Soluções Completas',
        title: {
          prefix: 'Soluções Abrangentes',
          highlight: 'de Trade-In'
        },
        subtitle: 'Ferramentas e serviços completos para cada etapa do ciclo de vida do dispositivo, da avaliação à revenda.',
        solutions: [
          {
            title: 'Trade-In para Consumidores',
            description: 'Obtenha cotações instantâneas para seus dispositivos usados com nossa tecnologia de avaliação por IA',
            features: ['Cotações instantâneas', 'Avaliação por IA', 'Pagamento seguro']
          },
          {
            title: 'Quiosques Autoatendimento',
            description: 'Sistemas de quiosque em loja para avaliação automatizada e crédito imediato de trade-in',
            features: ['Disponível 24/7', 'Crédito instantâneo', 'Fácil instalação']
          },
          {
            title: 'Diagnóstico por IA',
            description: 'Diagnósticos visuais e funcionais avançados para avaliar com precisão a condição do dispositivo',
            features: ['Inspeção visual', 'Teste de componentes', 'Verificação IMEI']
          },
          {
            title: 'Plataforma Empresarial',
            description: 'Ferramentas abrangentes para varejistas e operadoras gerenciarem programas de trade-in',
            features: ['Análises', 'Gestão de estoque', 'Relatórios']
          },
          {
            title: 'Mercado Atacadista',
            description: 'Conecte-se com compradores e vendedores verificados em nosso marketplace global',
            features: ['Compradores verificados', 'Negociações seguras', 'Alcance global']
          },
          {
            title: 'Plataforma de Leilões',
            description: 'Maximize o valor dos dispositivos através de lances competitivos em nosso ambiente de leilões',
            features: ['Lances ao vivo', 'Lotes grandes', 'Insights de mercado']
          }
        ]
      },
      // Advanced Features
      advancedFeatures: {
        badge: 'RECURSOS AVANÇADOS',
        title: {
          prefix: 'Tecnologia Poderosa com',
          highlight: 'Design Intuitivo'
        },
        subtitle: 'Nossa plataforma combina tecnologia de IA de ponta com interfaces amigáveis para oferecer uma experiência de trade-in perfeita.',
        features: [
          {
            title: 'Avaliação por IA',
            description: 'Obtenha o melhor valor para seus eletrônicos usados com nossa tecnologia avançada de diagnóstico visual',
            features: ['Detecção de danos visuais', 'Teste de componentes', 'Análise de valor de mercado']
          },
          {
            title: 'Verificação Confiável',
            description: 'Verificações abrangentes de IMEI e detecção de bloqueio garantem transações seguras e confiáveis',
            features: ['Verificação IMEI', 'Verificação de lista negra', 'Validação de propriedade']
          },
          {
            title: 'Marketplace Atacadista',
            description: 'Conecte-se com compradores e vendedores premium em nosso marketplace global de dispositivos',
            features: ['Acesso à rede global', 'Transações seguras', 'Preços competitivos']
          }
        ],
        stats: [
          {
            value: '99,9%',
            label: 'Disponibilidade'
          },
          {
            value: '50M+',
            label: 'Dispositivos Processados'
          },
          {
            value: '95%',
            label: 'Taxa de Precisão'
          },
          {
            value: '2M+',
            label: 'Negociações Mensais'
          }
        ]
      },
      // Trade-in Widget
      tradeInWidget: {
        title: 'Rápido & Fácil',
        heading: 'Experiência de Trade-In Simplificada',
        subtitle: 'Obtenha uma cotação instantânea para seu dispositivo em poucos passos. Nosso processo é projetado para ser rápido, transparente e conveniente.',
        steps: {
          deviceType: {
            title: 'Selecione o Tipo de Dispositivo',
            smartphone: 'Smartphone',
            tablet: 'Tablet'
          },
          brand: {
            title: 'Selecione a Marca',
            options: {
              apple: 'Apple',
              samsung: 'Samsung',
              google: 'Google',
              other: 'Outro'
            }
          },
          model: {
            title: 'Selecione o Modelo',
            options: {
              iphone13: 'iPhone 13 Pro',
              iphone12: 'iPhone 12',
              iphone11: 'iPhone 11',
              other: 'Outro'
            }
          },
          condition: {
            title: 'Condição do Dispositivo',
            options: {
              excellent: {
                title: 'Excelente',
                desc: 'Como novo, sem arranhões ou danos'
              },
              good: {
                title: 'Bom',
                desc: 'Pequenos arranhões, totalmente funcional'
              },
              fair: {
                title: 'Regular',
                desc: 'Desgaste visível, funciona adequadamente'
              }
            }
          },
          quote: {
            title: 'Sua Cotação Estimada',
            deviceInfo: 'Este é um valor estimado de trade-in para seu',
            condition: 'em condição',
            condition_suffix: '',
            buttons: {
              accept: 'Aceitar Oferta',
              tryAnother: 'Tentar Outro Dispositivo'
            }
          }
        },
        whyUs: {
          title: 'Por que fazer Trade-In conosco?',
          features: [
            {
              title: 'Rápido & Conveniente',
              description: 'Obtenha uma cotação instantânea em minutos. Sem espera, sem complicação.'
            },
            {
              title: 'Melhor Valor Garantido',
              description: 'Nossa precificação com IA garante que você receba o maior valor de mercado pelo seu dispositivo.'
            },
            {
              title: 'Seguro & Confiável',
              description: 'Seus dados são apagados com segurança e todas as transações são protegidas.'
            }
          ]
        }
      },
      // AI Assessment
      aiAssessment: {
        title: 'Avaliação por IA',
        subtitle: 'Nossa tecnologia avançada de IA avalia com precisão a condição do dispositivo, garantindo valores justos e competitivos de trade-in.',
        features: [
          'Inspeção visual detecta arranhões, amassados e danos na tela',
          'Testes funcionais para todos os componentes',
          'Algoritmos de precificação baseados no mercado',
          'Cotações instantâneas em menos de 2 minutos'
        ]
      },
      // Diagnostic Platform
      diagnosticPlatform: {
        badge: 'Veja o DiagnostiX em ação',
        subtitle: 'Nossa plataforma abrangente de diagnóstico fornece informações detalhadas do dispositivo, verificação de IMEI e testes de componentes para garantir que cada dispositivo seja avaliado adequadamente.',
        device: {
          info: {
            title: 'Informações do Dispositivo',
            model: 'Modelo',
            storage: 'Armazenamento',
            color: 'Cor',
            imei: 'IMEI'
          },
          verification: {
            title: 'Verificação de IMEI',
            status: {
              clean: 'Status Limpo',
              notBlacklisted: 'Não Bloqueado',
              notLost: 'Não Reportado como Perdido',
              complete: 'Verificação Completa'
            }
          },
          testing: {
            title: 'Teste de Componentes',
            components: {
              battery: 'Bateria',
              camera: 'Câmera',
              wifi: 'WiFi',
              bluetooth: 'Bluetooth',
              speakers: 'Alto-falantes',
              charging: 'Carregamento',
              screen: 'Tela',
              vibration: 'Vibração'
            },
            status: {
              passed: 'Aprovado',
              warning: 'Atenção',
              failed: 'Falha'
            }
          },
          condition: {
            title: 'Condição Física',
            grade: 'Classificação Geral',
            details: [
              'Pequenos arranhões na parte traseira',
              'Pequena amassado na borda inferior',
              'Tela em excelente condição'
            ]
          }
        }
      },
      // Management Dashboard
      managementDashboard: {
        badge: 'PLATAFORMA SAAS',
        title: 'Gestão Completa de Trade-In',
        subtitle: 'Dashboard unificado com diagnósticos e relatórios',
        description: 'Nossa plataforma SaaS abrangente oferece fácil supervisão dos programas de trade-in com análises poderosas e ferramentas de gestão.',
        features: [
          'Gestão de clientes',
          'Ferramentas de avaliação',
          'Gestão de estoque',
          'Rastreamento de inventário',
          'Processamento de transações',
          'Relatórios e análises',
          'Módulo de autoatendimento'
        ],
        cta: 'Solicitar Demo'
      },
      // Syndicate Auctions
      syndicateAuctions: {
        badge: 'Poder de Compra Coletivo',
        title: {
          prefix: 'Leilões em',
          highlight: 'Sindicato'
        },
        subtitle: 'Una forças com outros compradores para desbloquear inventário premium e maiores lucros',
        whatIs: {
          title: 'O que é um',
          highlight: 'Sindicato',
          description: 'Um sindicato no mundo dos leilões de dispositivos móveis refere-se a múltiplos compradores reunindo fundos para fazer lances colaborativamente, permitindo a compra de lotes maiores de dispositivos, acesso a melhores preços e compartilhamento de inventário com base em termos pré-acordados.'
        },
        benefits: [
          {
            title: 'Menor Requisito de Capital',
            description: 'Reúna recursos com outros compradores para acessar lotes maiores além do poder de compra individual.'
          },
          {
            title: 'Melhor Economia de Escala',
            description: 'Ganhe poder de negociação para obter preços unitários mais baixos e melhores taxas de envio.'
          },
          {
            title: 'Exposição Reduzida ao Risco',
            description: 'Compartilhe o risco de inventário defeituoso ou de baixa demanda entre vários membros do sindicato.'
          },
          {
            title: 'Acesso a Inventário Premium',
            description: 'Mire em lotes de dispositivos de maior qualidade que seriam financeiramente inacessíveis individualmente.'
          }
        ],
        structure: {
          title: 'Estrutura do Sindicato',
          roles: [
            {
              title: 'Líder/Organizador',
              description: 'Inicia o sindicato, gerencia estratégia de lances e cuida da logística'
            },
            {
              title: 'Contribuintes Financeiros',
              description: 'Reúnem capital, definem orçamentos e determinam níveis de tolerância ao risco'
            },
            {
              title: 'Parceiros de Distribuição',
              description: 'Cuidam da revenda no mercado local, canais online ou reparos especializados'
            }
          ]
        }
      },
      // Footer
      footer: {
        description: 'O futuro da tecnologia de trade-in no atacado. Capacitando empresas com insights de mercado baseados em IA.',
        sections: {
          solutions: {
            title: 'Soluções',
            links: [
              { label: 'Trade-In para Consumidores', href: '#consumer-trade-in' },
              { label: 'Plataforma Empresarial', href: '#business-platform' },
              { label: 'Marketplace Atacadista', href: '#wholesale-marketplace' },
              { label: 'Plataforma de Leilões', href: '#auctions' }
            ]
          },
          company: {
            title: 'Empresa',
            links: [
              { label: 'Sobre Nós', href: '#about' },
              { label: 'Carreiras', href: '#careers' },
              { label: 'Imprensa', href: '#press' },
              { label: 'Contato', href: '#contact' }
            ]
          },
          legal: {
            title: 'Legal',
            links: [
              { label: 'Política de Privacidade', href: '#privacy' },
              { label: 'Termos de Serviço', href: '#terms' },
              { label: 'Política de Cookies', href: '#cookies' }
            ]
          }
        },
        copyright: '© 2024 R3UP. Todos os direitos reservados.'
      }
    }
  }
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('preferredLanguage') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  })

export default i18n